import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  PlaidEmbeddedLink,
} from 'react-plaid-link';
import { connect } from 'react-redux';
import { capitalize } from 'lodash';
import { Alert } from 'react-bootstrap';
import {
  connectPlaidAccount as connectPlaidAccountAction,
  updatePlaidStatus as updatePlaidStatusAction,
  getPlaidAccountData,
  getPlaidStatus,
  getPlaidError,
} from '../../../../../../src/web/store/modules/movein';
import Icon from '../../../../../../src/web/components/Icon/Icon';
import PlaidIcon from './PlaidIcon';
import Translation from '../../../../../components/stateless/Translation';
import { PLAID_STATUS } from '../../../../../../constants/plaid';

const propTypes = {
  plaidLinkTokenData: PropTypes.shape(),
  connectPlaidAccount: PropTypes.func.isRequired,
  plaidAccountData: PropTypes.shape(),
  plaidStatus: PropTypes.string,
  plaidError: PropTypes.string,
  initiatePlaidLinkToken: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  updatePlaidStatus: PropTypes.func.isRequired,
};

const defaultProps = {
  plaidLinkTokenData: undefined,
  plaidAccountData: undefined,
  plaidStatus: PLAID_STATUS.NotStarted,
  plaidError: undefined,
};

const PlaidLink = ({
  plaidLinkTokenData,
  connectPlaidAccount,
  plaidAccountData,
  plaidStatus,
  plaidError,
  initiatePlaidLinkToken,
  setFieldValue,
  updatePlaidStatus,
}) => {
  const onLoad = useCallback(
    () => {
      updatePlaidStatus(PLAID_STATUS.Connecting);
    },
    [updatePlaidStatus, PLAID_STATUS.Connecting],
  );

  const onSuccess = useCallback(
    (publicToken) => {
      connectPlaidAccount(publicToken).then(({ data }) => {
        setFieldValue('bankName', data.connectPlaidAccount.institutionName);
        setFieldValue('accountType', 'Checking'); // Hardcoded to Checking for compatibility and StoPay doesn't care about the account type
        setFieldValue('routingNumber', data.connectPlaidAccount.accountRouting);
        setFieldValue('accountNumber', data.connectPlaidAccount.accountNumber);
        setFieldValue('accountNumberConfirmation', data.connectPlaidAccount.accountNumber);
      }).catch((error) => {
        console.error(error);
      });
    },
    [connectPlaidAccount, setFieldValue],
  );

  const onEvent = useCallback(
    (eventName, metadata) => {
      // Detect stale workflow session errors — trigger re-initialization
      if (
        eventName === 'ERROR'
        && (metadata.error_code === 'INVALID_FIELD' || metadata.error_message?.includes('invalid workflow session'))
      ) {
        initiatePlaidLinkToken();
      }
    },
    [initiatePlaidLinkToken],
  );

  const onExit = useCallback(
    (error) => {
      // Check if link token has expired - automatically refresh without user noticing
      if (error && (error.error_code === 'INVALID_LINK_TOKEN' || error.error_code === 'EXPIRED_LINK_TOKEN')) {
        initiatePlaidLinkToken();
      }
    },
    [initiatePlaidLinkToken],
  );

  const config = {
    onLoad,
    onSuccess,
    onExit,
    onEvent,
    token: plaidLinkTokenData?.linkToken,
  };

  return (
    <div>
      {plaidStatus === PLAID_STATUS.NotStarted && (
        <div className="sr-only">
          <Translation
            id="widgets.checkout.plaid.preparingConnection"
            defaultMessage="Preparing Plaid connection."
          />
        </div>
      )}
      {plaidStatus === PLAID_STATUS.LoadingLinkToken && (
        <div className="w-full mb-6">
          <div className="bg-blue-50 rounded-lg border-2 border-blue-200 p-6 flex items-center">
            <div className="flex items-center gap-4">
              <div className="rounded-full p-1.5 bg-blue-100">
                <div className="animate-spin h-8 w-8 border-[3px] border-blue-600 border-t-transparent rounded-full" />
              </div>
              <div>
                <div className="font-semibold text-gray-900">
                  <Translation
                    id="widgets.checkout.plaid.loadingLinkToken"
                    defaultMessage="Connecting to Plaid. Please wait."
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {plaidStatus === PLAID_STATUS.ReadyToConnect && (
        <div className="sr-only">
          <Translation
            id="widgets.checkout.plaid.readyToConnect"
            defaultMessage="Opening Plaid connection."
          />
        </div>
      )}
      {plaidStatus === PLAID_STATUS.Connecting && (
        <div className="sr-only">
          <Translation
            id="widgets.checkout.plaid.connecting"
            defaultMessage="Plaid is open. Select your bank and sign in to continue."
          />
        </div>
      )}
      {(plaidStatus === PLAID_STATUS.ReadyToConnect || plaidStatus === PLAID_STATUS.Connecting) && (
        <PlaidEmbeddedLink
          {...config}
          style={{ height: '450px', width: '100%' }}
        />
      )}
      {plaidStatus === PLAID_STATUS.VerifyingAccount && (
        <div className="w-full mb-6">
          <div className="bg-blue-50 rounded-lg border-2 border-blue-200 p-6 flex items-center">
            <div className="flex items-center gap-4">
              <div className="rounded-full p-1.5 bg-blue-100">
                <div className="animate-spin h-8 w-8 border-[3px] border-blue-600 border-t-transparent rounded-full" />
              </div>
              <div>
                <div className="font-semibold text-gray-900">
                  <Translation
                    id="widgets.checkout.plaid.verifyingTitle"
                    defaultMessage="Verifying account..."
                  />
                </div>
                <div className="text-sm text-gray-600 mt-0.5">
                  <Translation
                    id="widgets.checkout.plaid.verifyingDescription"
                    defaultMessage="Please wait while we connect to your bank account."
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {plaidStatus === PLAID_STATUS.Connected && (
        <div className="w-full mb-6">
          <div className="flex items-center gap-2 mb-4 text-sm text-[#185000] font-bold">
            <span className="fa fa-lock text-[#185000]" aria-hidden="true" />
            <span>
              <Translation
                id="widgets.checkout.plaid.connectedTitle"
                defaultMessage="Account verified and encrypted"
              />
            </span>
          </div>
          <div className="bg-[#f3ffee] rounded-lg border border-sm-green p-6 flex items-start justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className="rounded-full bg-sm-green p-2.5 ring-4 ring-[#90BF7C] flex items-center justify-center flex-shrink-0">
                <span className="fa fa-check text-white" />
              </div>
              <div>
                <div className="font-semibold text-gray-900">
                  {capitalize(plaidAccountData.accountSubtype)} {plaidAccountData.accountNumberMask}
                </div>
                <div className="text-sm text-gray-600 mt-0.5">
                  {plaidAccountData.institutionName}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="text-sm text-gray-600 hover:text-gray-900 uppercase"
                onClick={initiatePlaidLinkToken}
              >
                <Icon icon="edit2" className="text-gray-600 mr-1" />
                <Translation
                  id="widgets.checkout.editButton"
                  defaultMessage="Edit"
                />
              </button>
            </div>
          </div>
          <div className="flex items-center justify-left">
            <div className="w-full flex items-center gap-2">
              <span>
                <Translation
                  id="widgets.checkout.plaid.secureConnectionVia"
                  defaultMessage="Secure connection via"
                />
              </span>
              <PlaidIcon className="w-5 h-5" />
              <span className="text-sm font-semibold text-gray-700 tracking-wide uppercase">Plaid</span>
            </div>
          </div>
        </div>
      )}
      {plaidStatus === PLAID_STATUS.Error && plaidError && (
        <div className="w-full mb-6">
          <Alert bsStyle="danger">
            <Translation
              id={plaidError}
              defaultMessage="An error occurred while connecting to your bank. Please try again."
            />
          </Alert>
          <button
            type="button"
            onClick={initiatePlaidLinkToken}
            className="bg-sm-btn-prim text-black border-2 border-black p-[7px] rounded-md font-semibold hover:bg-sm-btn-prim-hov"
          >
            <Translation
              id="widgets.checkout.plaid.retry"
              defaultMessage="Try again"
            />
          </button>
        </div>
      )}
    </div>
  );
};

PlaidLink.propTypes = propTypes;
PlaidLink.defaultProps = defaultProps;

const mapStateToProps = state => ({
  plaidAccountData: getPlaidAccountData(state),
  plaidStatus: getPlaidStatus(state),
  plaidError: getPlaidError(state),
});

const mapDispatchToProps = dispatch => ({
  connectPlaidAccount: publicToken => dispatch(connectPlaidAccountAction(publicToken)),
  updatePlaidStatus: plaidStatus => dispatch(updatePlaidStatusAction(plaidStatus)),
});

export default connect(mapStateToProps, mapDispatchToProps)(PlaidLink);
